/**
 * Help chat (Chat de dúvidas) window events, screen labelling and per-user session state.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getHelpConfig } from '@/services/helpService';

export const HELP_OPEN_EVENT = 'help-chat:open';

/** sessionStorage keys: the panel's open state, the current conversation, and the user they belong to. */
export const HELP_OPEN_KEY = 'help:open';
export const HELP_CONVERSATION_KEY = 'help:conversation';
const HELP_USER_KEY = 'help:user';

/**
 * Opens the help chat. With text, starts a new conversation with that text in the input (and no
 * attached screenshot); without text, just opens the panel on the current conversation and draft.
 */
export function openHelpChat(prefill = ''): void {
  window.dispatchEvent(new CustomEvent<string>(HELP_OPEN_EVENT, { detail: prefill }));
}

/** Human-readable screen label sent to the API (never includes real ids). */
export function screenLabel(pathname: string): string {
  if (pathname === '/') return 'chat (/)';
  if (pathname === '/projects') return 'projects (/projects)';
  if (pathname === '/editor' || pathname.startsWith('/editor/')) return 'editor (/editor/:id)';
  if (pathname === '/meus-planos') return 'plans (/meus-planos)';
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return `admin (${pathname})`;
  return pathname;
}

export function readHelpSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeHelpSession(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

// One config request per user per page load.
let configPromise: Promise<boolean> | null = null;
let configUserId: string | null = null;

function loadHelpEnabled(userId: string): Promise<boolean> {
  if (configUserId !== userId) {
    configPromise = null;
    configUserId = userId;
  }
  if (!configPromise) {
    const promise: Promise<boolean> = getHelpConfig()
      .then((c) => c.enabled)
      .catch(() => {
        if (configPromise === promise) configPromise = null; // retry on the next page view
        return false;
      });
    configPromise = promise;
  }
  return configPromise;
}

/** Forgets the cached config and the tab's chat state. Call on logout and when the user changes. */
export function resetHelpSession(): void {
  configPromise = null;
  configUserId = null;
  for (const key of [HELP_OPEN_KEY, HELP_CONVERSATION_KEY, HELP_USER_KEY]) writeHelpSession(key, null);
}

/** Whether the help chat is on for the signed-in user; null while loading (or signed out). */
export function useHelpEnabled(): boolean | null {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [loaded, setLoaded] = useState<{ userId: string; enabled: boolean } | null>(null);

  useEffect(() => {
    if (!userId) return;
    // The tab's chat state belongs to one user: another login in the same tab starts clean.
    const owner = readHelpSession(HELP_USER_KEY);
    if (owner !== userId) {
      if (owner) resetHelpSession();
      writeHelpSession(HELP_USER_KEY, userId);
    }
    let alive = true;
    loadHelpEnabled(userId).then((enabled) => {
      if (alive) setLoaded({ userId, enabled });
    });
    return () => {
      alive = false;
    };
  }, [userId]);

  return userId && loaded?.userId === userId ? loaded.enabled : null;
}
